import * as THREE from 'three/webgpu';
import {Observable, ReplaySubject} from 'rxjs';
import type { SketchGroup, SketchPointObject } from '../../mesh/sketch';
import type { PointSelectionCandidate } from '../../reference/reference-point/ReferencePointController';
import type { SketchRuntimeHistorySession, Viewer } from '../../viewer/Viewer';
import { SketchEntitiesTransformGeometryService } from './SketchEntitiesTransformGeometryService';
import { SketchEntitiesTransformSelectionService } from './SketchEntitiesTransformSelectionService';
import {
	SketchEntitiesTransformInputMode,
	SketchEntitiesTransformPhase,
	SketchEntitiesTransformRelationRetentionMode,
	SketchEntitiesTransformSelectionRole,
	type SketchEntitiesTransformCommand,
	type SketchEntitiesTransformControllerSnapshot,
	type SketchEntitiesTransformPointItem,
	type SketchEntitiesTransformRepeatRecord,
} from './SketchEntitiesTransformTypes';

/**
 * 草图实体变换抽象控制器。
 */
export abstract class AbstractSketchEntitiesTransformController {
	/** Viewer 实例。 */
	protected readonly viewer: Viewer;
	/** 选择服务。 */
	protected readonly selectionService: SketchEntitiesTransformSelectionService = new SketchEntitiesTransformSelectionService();
	/** 几何服务。 */
	protected readonly geometryService: SketchEntitiesTransformGeometryService = new SketchEntitiesTransformGeometryService();
	/** 控制器快照事件流。 */
	private readonly snapshotSubject = new ReplaySubject<SketchEntitiesTransformControllerSnapshot>(1);

	/** 当前是否激活。 */
	protected active = false;
	/** 当前阶段。 */
	protected phase: SketchEntitiesTransformPhase = SketchEntitiesTransformPhase.Cancelled;
	/** 当前输入模式。 */
	protected inputMode: SketchEntitiesTransformInputMode = SketchEntitiesTransformInputMode.FromTo;
	/** 当前选择角色。 */
	protected activeSelectionRole: SketchEntitiesTransformSelectionRole = SketchEntitiesTransformSelectionRole.Entities;
	/** 几何关系保留模式。 */
	protected relationRetentionMode: SketchEntitiesTransformRelationRetentionMode = SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations;
	/** 当前 Viewer 生命周期内缓存的几何关系保留模式。 */
	private relationRetentionModeSessionCache = SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations;
	/** 已选草图实体。 */
	protected selectedEntities: THREE.Object3D[] = [];
	/** From 点。 */
	protected fromPoint: SketchEntitiesTransformPointItem | null = null;
	/** To 点。 */
	protected toPoint: SketchEntitiesTransformPointItem | null = null;
	/** 旋转中心。 */
	protected rotationCenter: SketchEntitiesTransformPointItem | null = null;
	/** X 输入值，移动实体中表示 X 方向平移距离。 */
	protected targetX: number | null = null;
	/** Y 输入值，移动实体中表示 Y 方向平移距离。 */
	protected targetY: number | null = null;
	/** 旋转角度。 */
	protected angleDeg: number | null = null;
	/** 预览历史会话。 */
	private previewSession: SketchRuntimeHistorySession | null = null;
	/** 最近一次重复记录。 */
	protected lastRepeatRecord: SketchEntitiesTransformRepeatRecord | null = null;

	/**
	 * @param viewer Viewer 实例。
	 * @param options 控制器选项。
	 */
	protected constructor(viewer: Viewer) {
		this.viewer = viewer;
		this.snapshotSubject.next(this.getSnapshot());
	}

	/**
	 * 启动命令。
	 *
	 * @param initialEntities 启动命令前已由普通草图选择链路选中的对象。
	 *
	 * 设计说明：
	 * 1. SOLIDWORKS 支持先选中草图实体再进入 Move / Copy / Rotate 命令；
	 * 2. UI 层进入命令时会清理普通选区，因此控制器必须在启动阶段吸收清理前的对象快照；
	 * 3. 预选对象仍复用选择服务解析，避免 UI 层直接理解轮廓、集合或草图边的底层结构。
	 */
	public start(initialEntities: readonly THREE.Object3D[] = []): void {
		this.rollbackPreview();
		this.active = true;
		this.phase = SketchEntitiesTransformPhase.SelectEntities;
		this.activeSelectionRole = SketchEntitiesTransformSelectionRole.Entities;
		this.relationRetentionMode = this.resolveInitialRelationRetentionMode();
		this.selectedEntities = this.resolveInitialSelectedEntities(initialEntities);
		this.fromPoint = null;
		this.toPoint = null;
		this.rotationCenter = null;
		this.targetX = null;
		this.targetY = null;
		this.angleDeg = null;
		this.syncSelectionVisuals(this.selectedEntities);
		this.refreshPhase();
		this.notifyStateChanged();
	}

	/**
	 * 清理命令。
	 */
	public clear(): void {
		this.rollbackPreview();
		this.active = false;
		this.phase = SketchEntitiesTransformPhase.Cancelled;
		this.activeSelectionRole = SketchEntitiesTransformSelectionRole.None;
		this.relationRetentionMode = this.resolveInitialRelationRetentionMode();
		this.selectedEntities = [];
		this.fromPoint = null;
		this.toPoint = null;
		this.rotationCenter = null;
		this.targetX = null;
		this.targetY = null;
		this.angleDeg = null;
		this.syncSelectionVisuals([]);
		this.notifyStateChanged();
	}

	/**
	 * 释放控制器。
	 */
	public dispose(): void {
		this.clear();
		this.snapshotSubject.complete();
	}

	/**
	 * 监听控制器快照变化。
	 *
	 * @returns 快照事件流。
	 */
	public observeSnapshot(): Observable<SketchEntitiesTransformControllerSnapshot> {
		return this.snapshotSubject.asObservable();
	}

	/**
	 * 判断命令是否激活。
	 *
	 * @returns 是否激活。
	 */
	public isActive(): boolean {
		return this.active;
	}

	/**
	 * 设置输入模式。
	 *
	 * @param mode 输入模式。
	 */
	public setInputMode(mode: SketchEntitiesTransformInputMode): void {
		this.inputMode = mode;
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
	}

	/**
	 * 设置当前选择角色。
	 *
	 * @param role 选择角色。
	 */
	public setActiveSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
		this.activeSelectionRole = role;
		this.refreshPhase();
		this.notifyStateChanged();
	}

	//------ 实体变换保留几何关系缓存 开始------
	/**
	 * 解析命令启动时使用的几何关系保留模式。
	 *
	 * @returns 启动命令时使用的几何关系保留模式。
	 *
	 * 设计说明：
	 * 1. 当前产品隐藏 UI 开关并默认不保留跨选择域几何关系；
	 * 2. 程序化调整后由同一个控制器实例缓存，后续重新进入 Move / Copy / Rotate 时恢复；
	 * 3. 初始化入口集中在 cad-core，避免 UI 层另存一份状态导致快照与底层不一致。
	 */
	protected resolveInitialRelationRetentionMode(): SketchEntitiesTransformRelationRetentionMode {
		return this.relationRetentionModeSessionCache;
	}

	/**
	 * 缓存几何关系保留模式。
	 *
	 * @param mode 几何关系保留模式。
	 *
	 * 设计说明：
	 * 1. 缓存范围与矩形绘制选项一致，限定在当前 Viewer / 控制器实例生命周期；
	 * 2. 页面刷新或 Viewer 重建会自然恢复默认值，不写入浏览器持久化存储；
	 * 3. 该方法供需要自定义预览重建流程的子类复用，避免重复维护缓存字段。
	 */
	protected cacheRelationRetentionMode(mode: SketchEntitiesTransformRelationRetentionMode): void {
		this.relationRetentionModeSessionCache = mode;
	}

	/**
	 * 设置几何关系保留模式。
	 *
	 * @param mode 几何关系保留模式。
	 *
	 * 设计说明：
	 * 1. 几何关系保留模式是命令级状态，必须由 cad-core 控制器托管；
	 * 2. 父类只负责保存状态和派发快照，具体命令如 Move 可覆写并重建预览；
	 * 3. 使用枚举而不是 boolean，避免后续 Copy / Rotate 扩展时语义漂移。
	 */
	public setRelationRetentionMode(mode: SketchEntitiesTransformRelationRetentionMode): void {
		this.cacheRelationRetentionMode(mode);
		this.relationRetentionMode = mode;
		this.notifyStateChanged();
	}
	//------ 实体变换保留几何关系缓存 结束------

	/**
	 * 设置 X/Y 输入值。
	 *
	 * @param axis 输入轴。
	 * @param value 输入值。
	 */
	public setTargetCoordinate(axis: 'x' | 'y', value: number | null): void {
		if (axis === 'x') {
			this.targetX = value;
		} else {
			this.targetY = value;
		}
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
	}

	/**
	 * 设置旋转角度。
	 *
	 * @param value 角度值。
	 */
	public setAngleDeg(value: number | null): void {
		this.angleDeg = value;
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
	}

	/**
	 * 处理实体选择。
	 *
	 * @param object 命中对象。
	 * @returns 是否已消费。
	 */
	public handleEntityPick(object: THREE.Object3D): boolean {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}

		const entity = this.resolveTransformEntity(object, activeSketchGroup);
		if (!entity) {
			return false;
		}

		this.rollbackPreview();
		const exists = this.selectedEntities.some((item) => item.uuid === entity.uuid);
		this.selectedEntities = exists
			? this.selectedEntities.filter((item) => item.uuid !== entity.uuid)
			: [...this.selectedEntities, entity];
		this.syncSelectionVisuals(this.selectedEntities);
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}

	//------ 草图实体变换框选 开始------
	/**
	 * 处理实体框选。
	 *
	 * @param objects 框选命中的原始对象集合。
	 * @returns 是否已消费。
	 *
	 * 设计说明：
	 * 1. 框选语义只追加新增命中对象，不应复用单击选择的反选逻辑；
	 * 2. 所有命中对象仍统一经过选择服务归一，避免 UI 或 selection handler 直接理解草图内部结构；
	 * 3. 只有真实新增实体时才回滚并刷新预览，避免重复框选已选实体导致预览抖动。
	 */
	public handleEntityBoxSelection(objects: readonly THREE.Object3D[]): boolean {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}

		const boxEntities = this.resolveTransformEntities(objects, activeSketchGroup);
		const selectedEntityMap = new Map(this.selectedEntities.map((entity) => [entity.uuid, entity]));
		const appendedEntities = boxEntities.filter((entity) => !selectedEntityMap.has(entity.uuid));
		if (appendedEntities.length === 0) {
			this.syncSelectionVisuals(this.selectedEntities);
			return true;
		}

		this.rollbackPreview();
		this.selectedEntities = [...this.selectedEntities, ...appendedEntities];
		this.syncSelectionVisuals(this.selectedEntities);
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}
	//------ 草图实体变换框选 结束------

	/**
	 * 处理参考点选择。
	 *
	 * @param candidate 参考点候选。
	 * @returns 是否已消费。
	 */
	public handleReferencePointSelection(candidate: PointSelectionCandidate): boolean {
		const pointItem = this.selectionService.buildPointItem(candidate);
		if (!pointItem) {
			return false;
		}

		this.rollbackPreview();
		switch (this.activeSelectionRole) {
			case SketchEntitiesTransformSelectionRole.FromPoint:
				this.fromPoint = pointItem;
				break;
			case SketchEntitiesTransformSelectionRole.ToPoint:
				this.toPoint = pointItem;
				break;
			case SketchEntitiesTransformSelectionRole.RotationCenter:
				this.rotationCenter = pointItem;
				break;
			default:
				return false;
		}
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}

	/**
	 * 删除指定已选实体。
	 *
	 * @param entityId 实体 ID。
	 */
	public removeSelectedEntity(entityId: string): void {
		this.rollbackPreview();
		this.selectedEntities = this.selectedEntities.filter((entity) => entity.uuid !== entityId);
		this.syncSelectionVisuals(this.selectedEntities);
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
	}

	/**
	 * 确认命令。
	 *
	 * @returns 是否成功。
	 */
	public confirm(): boolean {
		if (!this.canConfirm()) {
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}

		const matrix = this.resolveTransformMatrix();
		this.rollbackPreview();
		this.viewer.executeActiveSketchRuntimeHistoryTransaction({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
			work: () => {
				this.applyResolvedTransform(matrix, activeSketchGroup);
				this.commitSketchGroup(activeSketchGroup);
				return true;
			},
		});
		this.lastRepeatRecord = this.createRepeatRecord(matrix);
		this.phase = SketchEntitiesTransformPhase.Committed;
		this.notifyStateChanged();
		return true;
	}

	/**
	 * 取消命令。
	 */
	public cancel(): void {
		this.rollbackPreview();
		this.phase = SketchEntitiesTransformPhase.Cancelled;
		this.notifyStateChanged();
	}

	/**
	 * 重复上一次变换。
	 *
	 * @returns 是否成功。
	 */
	public repeat(): boolean {
		if (!this.lastRepeatRecord || !this.canRepeat()) {
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}

		this.rollbackPreview();
		const succeeded = this.applyRepeatRecord(this.lastRepeatRecord, activeSketchGroup);
		if (succeeded) {
			this.phase = SketchEntitiesTransformPhase.Committed;
			this.notifyStateChanged();
		}
		return succeeded;
	}

	/**
	 * 读取当前快照。
	 *
	 * @returns 快照对象。
	 */
	public getSnapshot(): SketchEntitiesTransformControllerSnapshot {
		return {
			active: this.active,
			command: this.resolveCommandType(),
			phase: this.phase,
			inputMode: this.inputMode,
			activeSelectionRole: this.activeSelectionRole,
			relationRetentionMode: this.relationRetentionMode,
			selectedItems: this.selectionService.buildSelectedItems(this.selectedEntities),
			fromPoint: this.fromPoint ? {...this.fromPoint, position: this.fromPoint.position.clone()} : null,
			toPoint: this.toPoint ? {...this.toPoint, position: this.toPoint.position.clone()} : null,
			rotationCenter: this.rotationCenter ? {...this.rotationCenter, position: this.rotationCenter.position.clone()} : null,
			mirrorAxis: null,
			copyEnabled: false,
			targetX: this.targetX,
			targetY: this.targetY,
			angleDeg: this.angleDeg,
			canConfirm: this.canConfirm(),
			canRepeat: this.canRepeat(),
		};
	}

	/**
	 * 解析命令类型。
	 *
	 * @returns 命令类型。
	 */
	protected abstract resolveCommandType(): SketchEntitiesTransformCommand;

	/**
	 * 解析变换矩阵。
	 *
	 * @returns 变换矩阵。
	 */
	protected abstract resolveTransformMatrix(): THREE.Matrix4;

	/**
	 * 应用变换。
	 *
	 * @param matrix 变换矩阵。
	 * @param sketchGroup 活动草图组。
	 */
	protected abstract applyResolvedTransform(matrix: THREE.Matrix4, sketchGroup: SketchGroup): void;

	/**
	 * 解析历史命令类型。
	 *
	 * @returns 历史命令类型。
	 */
	protected abstract resolveHistoryCommandType(): string;

	/**
	 * 解析历史标签。
	 *
	 * @returns 历史标签。
	 */
	protected abstract resolveHistoryLabel(): string;

	/**
	 * 判断是否允许确认。
	 *
	 * @returns 是否允许确认。
	 */
	protected abstract canConfirm(): boolean;

	/**
	 * 判断是否允许重复。
	 *
	 * @returns 是否允许重复。
	 */
	protected abstract canRepeat(): boolean;

	/**
	 * 应用重复记录。
	 *
	 * @param record 重复记录。
	 * @param sketchGroup 活动草图组。
	 * @returns 是否成功。
	 */
	protected abstract applyRepeatRecord(record: SketchEntitiesTransformRepeatRecord, sketchGroup: SketchGroup): boolean;

	/**
	 * 创建重复记录。
	 *
	 * @param matrix 变换矩阵。
	 * @returns 重复记录。
	 */
	protected createRepeatRecord(matrix: THREE.Matrix4): SketchEntitiesTransformRepeatRecord | null {
		if (this.selectedEntities.length === 0) {
			return null;
		}
		return {
			sourceEntityIds: this.selectedEntities.map((entity) => entity.uuid),
			matrix: matrix.clone(),
		};
	}

	/**
	 * 读取活动草图组。
	 *
	 * @returns 活动草图组。
	 */
	protected resolveActiveSketchGroup(): SketchGroup | null {
		return this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
	}

	/**
	 * 通过统一参数系统把实体定义点应用矩阵变换。
	 *
	 * @param entities 本次变换实体域。
	 * @param matrix 目标变换矩阵。
	 * @param intentId 当前交互内稳定的操作意图 ID。
	 * @param baseline 可选的点位基线；提供时从基线计算绝对目标，避免预览逐帧累计。
	 * @returns 完整活动草图参数岛是否求解并写回成功。
	 */
	protected applyParametricTransform(
		entities: readonly THREE.Object3D[],
		matrix: THREE.Matrix4,
		intentId: string,
		baseline?: ReadonlyMap<string, THREE.Vector3>,
	): boolean {
		const targets = new Map<SketchPointObject, THREE.Vector3>();
		this.geometryService.collectParametricRigidTransformPoints(entities).forEach((point) => {
			const sourcePosition = baseline?.get(point.uuid) ?? point.getPosition();
			targets.set(point, sourcePosition.clone().applyMatrix4(matrix));
		});
		return this.viewer.previewSketchParametricPointTargets(targets, intentId);
	}

	/**
	 * 通过统一参数系统恢复实体定义点基线。
	 *
	 * @param entities 本次变换实体域。
	 * @param baseline 点运行时 ID 到原始世界坐标的映射。
	 * @param intentId 回滚操作意图 ID。
	 * @returns 原始点位是否通过完整活动草图参数求解。
	 */
	protected restoreParametricBaseline(
		entities: readonly THREE.Object3D[],
		baseline: ReadonlyMap<string, THREE.Vector3>,
		intentId: string,
	): boolean {
		const targets = new Map<SketchPointObject, THREE.Vector3>();
		this.geometryService.collectParametricRigidTransformPoints(entities).forEach((point) => {
			const sourcePosition = baseline.get(point.uuid);
			if (sourcePosition) {
				targets.set(point, sourcePosition.clone());
			}
		});
		return this.viewer.previewSketchParametricPointTargets(targets, intentId);
	}

	//------ 草图实体变换预选实体吸收 开始------
	/**
	 * 解析命令启动前的预选草图实体。
	 *
	 * @param initialEntities 普通选择链路中的对象快照。
	 * @returns 可被当前变换命令消费的草图实体列表。
	 *
	 * 设计说明：
	 * 1. 普通选择可能选中集合、轮廓代理、边、点或这些对象的子级对象；
	 * 2. 这里统一交给 `SketchEntitiesTransformSelectionService` 解析，保持与命令内点击选择一致；
	 * 3. 按 uuid 去重，避免轮廓高亮和边对象同时存在时重复移动同一个实体。
	 */
	private resolveInitialSelectedEntities(initialEntities: readonly THREE.Object3D[]): THREE.Object3D[] {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup || initialEntities.length === 0) {
			return [];
		}

		return this.resolveTransformEntities(initialEntities, activeSketchGroup);
	}
	//------ 草图实体变换预选实体吸收 结束------

	//------ 草图实体变换框选 开始------
	/**
	 * 解析可被实体变换命令消费的草图实体。
	 *
	 * @param objects 原始对象集合。
	 * @param activeSketchGroup 当前活动草图组。
	 * @returns 去重后的可变换实体集合。
	 *
	 * 设计说明：
	 * 1. 预选和框选都需要把轮廓、集合、子对象归一为真实可变换实体；
	 * 2. 归一逻辑集中在父类，避免 Move / Copy / Rotate 三个命令重复实现；
	 * 3. 按 uuid 去重，确保完整框选矩形时共享边或轮廓代理不会重复进入变换集合。
	 */
	protected resolveTransformEntities(
		objects: readonly THREE.Object3D[],
		activeSketchGroup: SketchGroup,
	): THREE.Object3D[] {
		return this.selectionService.resolveTransformEntities(objects, activeSketchGroup);
	}
	//------ 草图实体变换框选 结束------

	/**
	 * 解析单个可被实体变换命令消费的草图实体。
	 *
	 * 设计说明：
	 * 1. 父类默认保留 Move / Rotate 既有的父级归一行为；
	 * 2. 具体命令可重写本入口收紧自身选择边界，而无需复制选择集切换、预览和状态通知逻辑。
	 *
	 * @param object 原始命中对象。
	 * @param activeSketchGroup 当前活动草图组。
	 * @returns 可变换草图实体；不允许选择时返回 null。
	 */
	protected resolveTransformEntity(
		object: THREE.Object3D,
		activeSketchGroup: SketchGroup,
	): THREE.Object3D | null {
		return this.selectionService.resolveTransformEntity(object, activeSketchGroup);
	}

	/**
	 * 刷新阶段状态。
	 */
	protected refreshPhase(): void {
		if (!this.active) {
			this.phase = SketchEntitiesTransformPhase.Cancelled;
			return;
		}
		if (this.canConfirm()) {
			this.phase = this.previewSession
				? SketchEntitiesTransformPhase.Previewing
				: SketchEntitiesTransformPhase.Ready;
			return;
		}
		this.phase = this.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
			? SketchEntitiesTransformPhase.SelectEntities
			: SketchEntitiesTransformPhase.SelectReference;
	}

	/**
	 * 尝试刷新预览。
	 */
	protected tryPreview(): void {
		if (!this.active) {
			return;
		}
		if (!this.canConfirm()) {
			this.rollbackPreview();
			return;
		}
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return;
		}

		this.rollbackPreview();
		this.previewSession = this.viewer.beginActiveSketchRuntimeHistorySession({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
		});
		this.applyResolvedTransform(this.resolveTransformMatrix(), activeSketchGroup);
		this.viewer.requestRender();
		this.refreshPhase();
	}

	/**
	 * 回滚当前预览。
	 */
	protected rollbackPreview(): void {
		if (!this.previewSession) {
			return;
		}
		this.previewSession.cancel();
		this.previewSession = null;
		this.viewer.requestRender();
		this.refreshPhase();
	}

	/**
	 * 提交草图组定义变更。
	 *
	 * @param sketchGroup 活动草图组。
	 */
	protected commitSketchGroup(sketchGroup: SketchGroup): void {
		const sourceObjectId = this.selectedEntities[0]?.uuid;
		sketchGroup.markDefinitionDirty({
			reason: this.resolveHistoryCommandType(),
			sourceObjectId,
		});
		sketchGroup.commitDefinitionChanges({
			reason: this.resolveHistoryCommandType(),
			sourceObjectId,
		});
		this.viewer.requestRender();
	}

	/**
	 * 同步选择可视化。
	 *
	 * @param selectedEntities 已选实体。
	 */
	protected syncSelectionVisuals(selectedEntities: readonly THREE.Object3D[]): void {
		const selectionManager = this.viewer.entitySelectionManager;
		if (!selectionManager) {
			return;
		}
		selectionManager.updateSelectionState(selectedEntities.map((entity) => ({
			distance: 0,
			object: entity,
			point: entity.getWorldPosition(new THREE.Vector3()),
		})));
	}

	/**
	 * 派发状态变化。
	 */
	protected notifyStateChanged(): void {
		this.snapshotSubject.next(this.getSnapshot());
	}
}
